import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import fetch from 'isomorphic-unfetch';

import Checkbox from '@components/atoms/checkbox/checkbox';
import Button from '../../atoms/button/button';
import Input from '../../atoms/input/input';
import Select from '../../atoms/select/select';
import Textarea from '../../atoms/textarea/textarea';
import Notification from '../../atoms/notification/notification';

import { fetchCsrfToken } from '../../../helper/api';
import MixedCopy from '../mixed-copy/mixedCopy';
import StyledFormCompact from './formCompact.style';

const validateEmail = value => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(value);
};

const validateText = value => value;

function Field(props) {
  const { className, fieldType, ...rest } = props;

  switch (fieldType) {
    case 'input':
      return <Input className={className} {...rest} />;
    case 'select':
      return <Select className={className} {...rest} />;
    case 'textarea':
      return <Textarea className={className} {...rest} />;
    case 'checkbox':
      return <Checkbox className={className} {...rest} />;
    case 'infoText':
      return <MixedCopy html={rest.copy} fullWidth className="infoText form__cell" />;
    default:
      return null;
  }
}

Field.propTypes = {
  fieldType: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

const FormCompact = ({
  id,
  withCsrfToken = false,
  cells,
  method = 'post',
  target = '_self',
  action,
  submitText = 'Abschicken',
  successMessage,
  onSubmit = () => {}
}) => {
  const t = useTranslations();

  // Generate initial form state based on provided cells
  const initialFormData = useMemo(() => {
    const initialData = {};
    cells.forEach(cell => {
      const error = !cell.required || (cell.options && cell.options[0].value) ? false : null;
      const value = cell.options ? cell.options[0].value : cell.value ? cell.value : '';
      initialData[cell.name] = { value, error, active: false };
    });
    return initialData;
  }, [cells]);

  const [formData, setFormData] = useState(initialFormData);
  const [csrfToken, setCsrfToken] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  const warnings = useMemo(
    () => ({
      text: t('formField.text'),
      email: t('formField.email')
    }),
    [t]
  );

  useEffect(() => {
    setDisabled(false);
    if (withCsrfToken) {
      fetchCsrfToken(
        token => setCsrfToken(token),
        err => console.log(err)
      );
    }
  }, [withCsrfToken]);

  useEffect(() => {
    let timeout;
    if (formSubmitted) {
      timeout = setTimeout(() => setFormSubmitted(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [formSubmitted]);

  // Error message timeout
  useEffect(() => {
    let timeout;
    if (formError) {
      timeout = setTimeout(() => setFormError(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [formError]);

  const getFormData = useCallback(
    recaptchaValue => {
      const formDataObject = {};
      Object.keys(formData).forEach(key => {
        formDataObject[key] = formData[key].value || null;
      });
      formDataObject['g-recaptcha-response'] = recaptchaValue;
      return formDataObject;
    },
    [formData]
  );

  const validateForm = useCallback(() => {
    let result = true;
    setFormData(prev => {
      const nextFormData = { ...prev };
      Object.keys(nextFormData).forEach(key => {
        if (nextFormData[key].error || nextFormData[key].error === null) {
          nextFormData[key] = { ...nextFormData[key], error: true };
          result = false;
        }
      });
      return nextFormData;
    });
    return result;
  }, []);

  const handleChangeField = useCallback(({ target: { name, value } }) => {
    setFormData(prev => ({
      ...prev,
      [name]: { ...prev[name], value }
    }));
  }, []);

  const handleFocusField = useCallback(({ target: { name } }) => {
    setFormData(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        active: true,
        error: prev[name].error !== false ? null : false
      }
    }));
  }, []);

  const handleBlurField = useCallback(({ target: { name, type, value } }) => {
    const checking = type === 'email' ? validateEmail(value) : validateText(value);
    setFormData(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        active: false,
        error: prev[name].error !== false ? !checking : false,
        value
      }
    }));
  }, []);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      const check = validateForm();
      const recaptchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : null;

      if (!recaptchaValue) {
        setRecaptchaError(true);
      }

      if (check && recaptchaValue) {
        if (withCsrfToken) {
          const payload = getFormData(recaptchaValue);

          fetch(action, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': csrfToken,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          })
            .then(res => res.json())
            .then(json => {
              if (json.message) {
                setFormSubmitted(true);
                setCsrfToken(json.token);
              } else if (json.error) {
                setFormError(true);
                setCsrfToken(json.token);
              }
            })
            .catch(json => {
              setFormError(true);
              setCsrfToken(json?.token || '');
            });
        } else {
          onSubmit(e);
          if (formRef.current) {
            formRef.current.action = action;
            formRef.current.submit();
            formRef.current.action = '';
          }
          setFormSubmitted(true);
        }

        // Reinitialize form
        if (formRef.current) formRef.current.reset();
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setFormData(initialFormData);
      }
    },
    [action, csrfToken, getFormData, initialFormData, onSubmit, validateForm, withCsrfToken]
  );

  return (
    <StyledFormCompact
      onSubmit={handleSubmit}
      id={id}
      ref={formRef}
      method={method}
      target={target}
      role="form"
      noValidate
    >
      <div className="form__inner">
        <div className="form__field-container">
          {cells.map(cell => {
            const { modifier, fieldType, name, type = 'text', required } = cell;

            let cellClassName = 'form__cell';
            cellClassName = modifier
              ? `${cellClassName} ${cellClassName}--${modifier}`
              : cellClassName;

            let validation = null;
            let events = null;
            let currentValue = null;

            if (fieldType !== 'button') {
              // Gracefully fallback if the cell isn't in state yet
              const cellState = formData[name] || { error: null, active: false, value: '' };
              const { error, active, value } = cellState;

              events = {
                onChange: handleChangeField,
                onBlur: handleBlurField,
                onFocus: handleFocusField,
                active
              };
              currentValue = value;

              if (required) {
                validation = { warning: warnings[type], error };
              }
            }

            return (
              <Field
                key={cell.id || name}
                className={cellClassName}
                value={currentValue}
                {...cell}
                {...events}
                {...validation}
              />
            );
          })}
        </div>
        <div className="form__cell form__submit">
          <div className="form__recaptcha-wrapper">
            <div className={`form__recaptcha ${recaptchaError ? 'has-error' : ''}`}>
              {process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY ? (
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY}
                  onChange={() => setRecaptchaError(false)}
                  ref={recaptchaRef}
                />
              ) : (
                console.error('RECAPCHA_SITEKEY is undefined in FormCompact')
              )}
              <p className="form__recaptcha-error">{t('form.recaptchaError')}</p>
            </div>
          </div>
          <div className="form__submit-button">
            <Button disabled={disabled} type="submit">
              {submitText}
            </Button>
          </div>
        </div>
      </div>

      {formSubmitted && (
        <Notification type="success">{successMessage || t('form.success')}</Notification>
      )}
      {formError && <Notification type="error">{t('form.error')}</Notification>}
    </StyledFormCompact>
  );
};

FormCompact.propTypes = {
  id: PropTypes.string.isRequired,
  withCsrfToken: PropTypes.bool,
  cells: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  method: PropTypes.string,
  target: PropTypes.string,
  action: PropTypes.string,
  submitText: PropTypes.string,
  successMessage: PropTypes.node,
  onSubmit: PropTypes.func
};

export default FormCompact;
